
import { createContext, useContext, useState, useEffect } from "react";
import { logger } from "@/services/LoggingService";

interface PortfolioSizeContextType {
  portfolioSize: number;
  setPortfolioSize: (size: number) => void;
  inputValue: string;
  setInputValue: (value: string) => void;
}

const DEFAULT_PORTFOLIO_SIZE = 500000;
const PORTFOLIO_SIZE_KEY = 'portfolioSize';
const SESSION_TIME_KEY = 'portfolioSessionTime';

const PortfolioSizeContext = createContext<PortfolioSizeContextType | undefined>(undefined);

export const usePortfolioSize = () => {
  const context = useContext(PortfolioSizeContext);
  if (!context) {
    throw new Error("usePortfolioSize must be used within a PortfolioSizeProvider");
  }
  return context;
};

export const PortfolioSizeProvider = ({ children }: { children: React.ReactNode }) => {
  // Check if this is a hard refresh by comparing session timestamps
  const lastSessionTime = sessionStorage.getItem(SESSION_TIME_KEY);
  const currentTime = Date.now();
  const isHardRefresh = !lastSessionTime || (sessionStorage?.length || 0) === 0;
  
  // Set new session timestamp
  sessionStorage.setItem(SESSION_TIME_KEY, currentTime.toString());
  
  logger.info("PortfolioSizeContext", "Session status", {
    isHardRefresh,
    lastSessionTime,
    currentTime,
    sessionStorageItems: sessionStorage.length
  });

  // Initialize state based on refresh type
  const [portfolioSize, setPortfolioSize] = useState<number>(() => {
    if (isHardRefresh) {
      logger.info("PortfolioSizeContext", "Hard refresh detected - clearing localStorage and using default", { 
        default: DEFAULT_PORTFOLIO_SIZE 
      });
      localStorage.removeItem(PORTFOLIO_SIZE_KEY);
      return DEFAULT_PORTFOLIO_SIZE;
    }
    
    const savedSize = localStorage.getItem(PORTFOLIO_SIZE_KEY);
    logger.info("PortfolioSizeContext", "Normal refresh - using localStorage value", { 
      savedSize 
    });
    return savedSize ? parseInt(savedSize) : DEFAULT_PORTFOLIO_SIZE;
  });
  
  const [inputValue, setInputValue] = useState<string>(() => {
    if (isHardRefresh) {
      return DEFAULT_PORTFOLIO_SIZE.toString();
    }
    const savedSize = localStorage.getItem(PORTFOLIO_SIZE_KEY);
    return savedSize || DEFAULT_PORTFOLIO_SIZE.toString();
  });

  // Update localStorage only when portfolioSize changes
  useEffect(() => {
    logger.debug("PortfolioSizeContext", "Updating localStorage with new portfolio size", { 
      portfolioSize 
    });
    localStorage.setItem(PORTFOLIO_SIZE_KEY, portfolioSize.toString());
  }, [portfolioSize]);

  return (
    <PortfolioSizeContext.Provider 
      value={{ 
        portfolioSize, 
        setPortfolioSize, 
        inputValue, 
        setInputValue 
      }}
    >
      {children}
    </PortfolioSizeContext.Provider>
  );
};
